// KSE-0195: authorization-code + PKCE; no client secret and no password exchange.
// Token material is returned to auth.tsx for sealing in the existing vault.
export interface OIDCConfiguration {
  issuer: string;
  clientId: string;
  jmapOrigin: string;
}
// Email domains publish this browser client contract next to their JMAP and
// native-client discovery. Lately carries no list of domains or identity providers.
export async function discoverSignIn(email: string): Promise<OIDCConfiguration | null> {
  const domain = email.split("@")[1]?.trim().toLowerCase();
  if (!domain || !/^[a-z0-9.-]+\.[a-z]{2,}$/.test(domain))
    throw new Error("Enter a full email address.");
  let response: Response;
  try {
    response = await fetch(`https://${domain}/.well-known/lately/web-application`, {
      credentials: "omit",
    });
  } catch {
    return null;
  } // Providers without browser discovery use JMAP/password.
  if (response.status === 404) return null;
  if (!response.ok) throw new Error("Your email provider's sign-in is unavailable.");
  if (!response.headers.get("content-type")?.includes("application/json")) return null;
  const app = await response.json();
  if (
    !Array.isArray(app.redirect_uris) ||
    !app.redirect_uris.includes(`${window.location.origin}/auth/callback`)
  ) {
    throw new Error("Your provider has not enabled Lately web sign-in.");
  }
  const configuration = {
    issuer: app.issuer,
    clientId: app.client_id,
    jmapOrigin: app.jmap_server_url,
  };
  validateConfiguration(configuration);
  return configuration;
}
export function validateConfiguration(configuration: OIDCConfiguration): void {
  if (typeof configuration.clientId !== "string" || !configuration.clientId)
    throw new Error("Missing OAuth client registration.");
  for (const value of [configuration.issuer, configuration.jmapOrigin]) {
    const url = new URL(value);
    if (url.protocol !== "https:" || url.username || url.password || url.hash || url.search)
      throw new Error("Invalid sign-in configuration.");
  }
}
const PENDING_KEY = "lately.oidc.pending.v1";

export interface OIDCSession extends OIDCConfiguration {
  refreshToken?: string;
  expiresAt: number;
}
export interface OIDCLogin {
  username: string;
  accessToken: string;
  session: OIDCSession;
}
interface Metadata {
  issuer: string;
  authorization_endpoint: string;
  token_endpoint: string;
  userinfo_endpoint: string;
}
interface Pending {
  state: string;
  verifier: string;
  redirectUri: string;
  createdAt: number;
  configuration: OIDCConfiguration;
}

function base64url(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}
export function validateMetadata(metadata: Metadata, issuer: string): Metadata {
  if (metadata.issuer !== issuer) throw new Error("Unexpected sign-in provider.");
  for (const endpoint of [
    metadata.authorization_endpoint,
    metadata.token_endpoint,
    metadata.userinfo_endpoint,
  ]) {
    if (
      new URL(endpoint).protocol !== "https:" ||
      new URL(endpoint).username ||
      new URL(endpoint).password
    ) {
      throw new Error("Unexpected sign-in endpoint.");
    }
  }
  return metadata;
}
async function discover(issuer: string): Promise<Metadata> {
  const response = await fetch(`${issuer.replace(/\/?$/, "/")}.well-known/openid-configuration`, {
    credentials: "omit",
  });
  if (!response.ok) throw new Error("The sign-in provider is unavailable.");
  return validateMetadata(await response.json(), issuer);
}
export async function beginSSO(
  configuration: OIDCConfiguration,
  loginHint?: string,
): Promise<void> {
  validateConfiguration(configuration);
  const metadata = await discover(configuration.issuer);
  const verifier = base64url(crypto.getRandomValues(new Uint8Array(32)));
  const pending: Pending = {
    state: base64url(crypto.getRandomValues(new Uint8Array(32))),
    verifier,
    redirectUri: `${window.location.origin}/auth/callback`,
    createdAt: Date.now(),
    configuration,
  };
  const challenge = base64url(
    new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier))),
  );
  sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
  const url = new URL(metadata.authorization_endpoint);
  url.search = new URLSearchParams({
    response_type: "code",
    client_id: configuration.clientId,
    redirect_uri: pending.redirectUri,
    scope: "openid profile email offline_access",
    state: pending.state,
    code_challenge: challenge,
    code_challenge_method: "S256",
    ...(loginHint ? { login_hint: loginHint } : {}),
  }).toString();
  window.location.assign(url);
}
export function validateCallback(
  params: URLSearchParams,
  pending: Pending | null,
  now = Date.now(),
): Pending {
  if (
    !pending ||
    params.get("state") !== pending.state ||
    now - pending.createdAt > 600_000 ||
    now < pending.createdAt
  ) {
    throw new Error("This sign-in has expired. Please sign in again.");
  }
  if (params.has("error") || !params.get("code"))
    throw new Error("Sign-in was not completed. Please try again.");
  return pending;
}
async function exchange(
  configuration: OIDCConfiguration,
  endpoint: string,
  parameters: Record<string, string>,
  previous?: OIDCSession,
) {
  const response = await fetch(endpoint, {
    method: "POST",
    credentials: "omit",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({ client_id: configuration.clientId, ...parameters }),
  });
  if (!response.ok)
    throw new Error("Your sign-in could not be renewed. Enter your email to sign in again.");
  const token = await response.json();
  if (
    typeof token.access_token !== "string" ||
    !token.access_token ||
    token.token_type?.toLowerCase() !== "bearer" ||
    typeof token.expires_in !== "number" ||
    !Number.isFinite(token.expires_in) ||
    token.expires_in <= 0
  )
    throw new Error("Invalid sign-in response.");
  return {
    accessToken: token.access_token as string,
    session: {
      ...configuration,
      refreshToken:
        typeof token.refresh_token === "string" ? token.refresh_token : previous?.refreshToken,
      expiresAt: Date.now() + token.expires_in * 1000,
    },
  };
}
// The code is consumed once, including under React StrictMode.
let callbackResult: Promise<OIDCLogin> | null = null;
export function completeSSO(): Promise<OIDCLogin> {
  if (callbackResult) return callbackResult;
  callbackResult = (async () => {
    const params = new URLSearchParams(window.location.search);
    const raw = sessionStorage.getItem(PENDING_KEY);
    sessionStorage.removeItem(PENDING_KEY);
    window.history.replaceState(null, "", "/");
    const pending = validateCallback(params, raw ? JSON.parse(raw) : null);
    if (pending.redirectUri !== `${window.location.origin}/auth/callback`)
      throw new Error("Invalid callback address.");
    validateConfiguration(pending.configuration);
    const metadata = await discover(pending.configuration.issuer);
    const tokens = await exchange(pending.configuration, metadata.token_endpoint, {
      grant_type: "authorization_code",
      code: params.get("code") ?? "",
      redirect_uri: pending.redirectUri,
      code_verifier: pending.verifier,
    });
    // Use the provider's authenticated userinfo response, never decoded JWT claims.
    const response = await fetch(metadata.userinfo_endpoint, {
      credentials: "omit",
      headers: { Authorization: `Bearer ${tokens.accessToken}` },
    });
    if (!response.ok) throw new Error("Could not verify your account.");
    const claims = await response.json();
    const username = claims.email || claims.preferred_username;
    if (typeof claims.sub !== "string" || typeof username !== "string" || !username)
      throw new Error("Sign-in did not identify a mail account.");
    return { username, ...tokens };
  })();
  return callbackResult;
}
export async function renewSSO(session: OIDCSession) {
  if (!session.refreshToken)
    throw new Error("Your session expired. Enter your email to sign in again.");
  validateConfiguration(session);
  const metadata = await discover(session.issuer);
  return exchange(
    session,
    metadata.token_endpoint,
    { grant_type: "refresh_token", refresh_token: session.refreshToken },
    session,
  );
}
